import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { workCompletionReportTokens } from "../../lib/database";
import { generateWorkCompletionReport } from "../customer-database/utils/generateWorkCompletionReport";

/**
 * Public page for downloading Work Completion Report PDF via token link (e.g. from Excel export).
 * No login required; security is the unguessable token (UUID) stored in Supabase.
 */
const WorkCompletionReportPublic = () => {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | ready | generating | done | error
  const [message, setMessage] = useState("");
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    const rawToken = token != null ? String(token).trim() : "";
    const decodedToken = rawToken ? decodeURIComponent(rawToken) : "";
    if (!decodedToken) {
      setStatus("error");
      setMessage("Invalid link.");
      return;
    }

    workCompletionReportTokens
      .getReportByToken(decodedToken)
      .then(({ data, error: rpcError }) => {
        if (rpcError) {
          setStatus("error");
          const msg = rpcError?.message || String(rpcError);
          setMessage(
            msg.includes("does not exist")
              ? "Report link setup incomplete. Please run the work completion report tokens SQL in Supabase."
              : msg,
          );
          return;
        }
        if (!data?.customer) {
          setStatus("error");
          setMessage("Report not found or link invalid.");
          return;
        }
        setPayload(data);
        setStatus("ready");
      })
      .catch((err) => {
        setStatus("error");
        setMessage(err?.message || "Failed to load work completion report.");
      });
  }, [token]);

  const handleDownload = async () => {
    if (!payload?.customer) return;
    try {
      setStatus("generating");
      await generateWorkCompletionReport(
        payload.customer,
        payload.survey || null,
        payload.civilWork || null,
        payload.technicalWork || null,
        payload.commissioningWork || null,
        payload.region || null,
        payload.cluster || null,
        payload.users || [],
      );
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setMessage(err?.message || "Failed to generate work completion report.");
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center">
          <p className="text-gray-600">Loading work completion report...</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center">
          <p className="text-red-600 font-medium">{message}</p>
        </div>
      </div>
    );
  }

  const customer = payload?.customer;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          <h1 className="text-lg md:text-xl font-semibold text-gray-900">
            Work Completion Report
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {customer?.name ? customer.name : "Customer"}
            {customer?.cin ? ` • CIN: ${customer.cin}` : ""}
            {customer?.usn ? ` • USN: ${customer.usn}` : ""}
          </p>

          <div className="mt-6">
            <button
              type="button"
              className="inline-flex items-center justify-center h-10 px-4 rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-60"
              onClick={handleDownload}
              disabled={status === "generating"}
            >
              {status === "generating"
                ? "Generating..."
                : status === "done"
                  ? "Download again"
                  : "Download PDF"}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              The PDF will download to your device.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkCompletionReportPublic;

